import { IconArrowsMaximize, IconArrowsMinimize, IconMinus, IconPlus, IconSend } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { ActionIcon, Avatar, Box, Flex, Group, Indicator, Text, Tooltip } from '@mantine/core';
import { useSelector } from 'react-redux';
import useAiChatResponse from '../../helpers/useAiChatResponse';
import useAiChatScroll from '../../helpers/useAiChatScroll';
import RelevaLogo from '../../logo_small.png';
import ChatBotInput from '../Chat/PromptInput/ChatBotPromptImput';
import PromptResponseList from '../Chat/PromptResponseList/PromptResponseList';
import ErrorMessage from '../ErrorMessage';

const model = {
  initialPrompt: '',
  advancedSettings: {
    safeImageGeneration: true,
    prompt_strength: 80,
    seed: 100000,
    temperature: 100,
    top_p: 95,
    system_prompt: 'You are a helpful assistant.',
    aspect_ratio: '1:1',
    width: null,
    height: null,
  },
  fastContextSelection: {
    coPilots: ['Releva Co-pilot'],
  },
};

const ChatBot = ({ data, dataError, handleChange, createContent, setIsOpen }) => {
  const { domain } = useSelector((state) => state.domain);

  const { responseList, status, error, prompt, streamState, handlePromptChange, getPrompt, abortPrompt } =
    useAiChatResponse(data, handleChange, model, domain, 'text');

  const messagesListRef = useRef(null);
  const aiChatScroll = useAiChatScroll(messagesListRef, responseList);

  const [expand, setExpand] = useState(false);
  const disabled = status === 'loading' || status === 'inputTooLong';

  return (
    <Box>
      <Box className={`chat-bot-container ${expand ? 'chat-bot-container-fullscreen' : ''}`}>
        <Flex className="chat-bot-header" justify="space-between" align="center">
          <Group gap="xs">
            <Indicator color="green" position="bottom-end" size={12} offset={4} withBorder processing>
              <Avatar
                src={RelevaLogo}
                alt="Lucy"
                size="lg"
                radius="xl"
                className="lucy-avatar"
                style={{ border: '2px solid rgba(255, 255, 255, 0.85)' }}
                imageProps={{ style: { objectFit: 'contain', padding: 6 } }}
              />
            </Indicator>
            <Box>
              <Text fw={700} c="white" size="lg">
                Lucy
              </Text>
              <Text size="xs" c="white" opacity={0.9}>
                Online
              </Text>
            </Box>
          </Group>
          <Group gap="xs">
            <Tooltip label="Start a new chat" position="bottom">
              <ActionIcon variant="filled" color="white" c="var(--mantine-color-primary-6)" onClick={() => createContent()}>
                <IconPlus size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label={expand ? 'Minimize chat' : 'Expand chat'} position="bottom">
              <ActionIcon variant="filled" color="white" c="var(--mantine-color-primary-6)" onClick={() => setExpand(!expand)}>
                {expand ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Close chat" position="bottom">
              <ActionIcon variant="filled" color="white" c="var(--mantine-color-primary-6)" onClick={() => setIsOpen(false)}>
                <IconMinus size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Flex>

        <Box ref={messagesListRef} className="chat-bot-messages-container" onScroll={aiChatScroll}>
          <Box id="response-list">
            <PromptResponseList
              responseList={responseList}
              isLoading={status === 'loading'}
              streamState={streamState}
              key="response-list"
              type="bot"
              onQuickReply={(instruction) => getPrompt(instruction)}
            />
          </Box>

          {(error || dataError) && <ErrorMessage message={`${error} ${dataError}`} />}
        </Box>

        <Box className="chat-bot-footer">
          <Flex
            gap="xs"
            align="center"
            opacity={disabled ? 0.6 : 1}
            bg="white"
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--mantine-color-gray-3)',
              padding: '4px 8px',
            }}
          >
            <Box style={{ flex: 1 }}>
              <ChatBotInput
                prompt={prompt}
                key="prompt-input"
                updatePrompt={handlePromptChange}
                onSubmit={() => !status !== 'inputTooLong' && getPrompt()}
                disabled={disabled}
              />
            </Box>

            <ActionIcon
              size="lg"
              radius="md"
              variant="filled"
              loading={status === 'loading'}
              disabled={disabled}
              onClick={() => {
                if (status !== 'inputTooLong') {
                  getPrompt();
                }
                if (status === 'prompting') {
                  abortPrompt();
                }
              }}
            >
              <IconSend size={18} />
            </ActionIcon>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatBot;
